/**
 * natureTextures — biome-tinted SVG generators for the three vegetation kinds
 * (tree / bush / grass). Each generator bakes the foliage tint into the SVG so
 * a single `loadTexture` per (kind, biome) pair yields a ready sprite — no
 * per-instance `setTint` needed (the trunk stays brown across biomes; only the
 * canopy re-skins). This is the "supply a texture" half of the renderer-seam
 * contract (SPEC.md §5); the SVG source is language-neutral so a Rust/wgpu
 * consumer can either generate the same SVG or supply real texture assets.
 *
 * Memoized by textureKey so repeated mounts of the same biome skip the
 * generation cost. Returns a base64 `data:image/svg+xml,...` dataUrl the
 * SpriteTextureSystem loads into the substrate cache.
 *
 * Environmental-prop kinds (palm_tree / beach_rock / …) delegate to
 * propTextures.generatePropSVG through the same cache + dataUrl convention —
 * see the seam decision in propTextures.ts.
 */
import { recipeFamilyFor } from './props/recipeFamilies';
import { townPropPalette } from './props/townPropTemplates';
import { generatePropSVG, isPropKind } from './propTextures';
import type { VegetationKind } from './natureConfig';
import type { TownPropPaletteOverrides } from '../../../shared/scene/townPropTemplates';

/** A memoized cache of generated dataUrls, keyed by textureKey. */
const dataUrlCache = new Map<string, string>();

/** Wrap raw SVG markup into the base64 dataUrl the sprite cache loads. */
function toDataUrl(svg: string): string {
  return 'data:image/svg+xml;base64,' + (typeof btoa !== 'undefined'
    ? btoa(unescape(encodeURIComponent(svg)))
    : Buffer.from(svg).toString('base64'));
}

/** Recover the biome id from a `nature_<kind>_<biome>_<tintHex>` texture key
 *  (kind and biome may both contain underscores — the kind is known and the
 *  tint hex is always the final segment). */
function biomeFromKey(textureKey: string, kind: string): string {
  textureKey = textureKey.split('__tpl_', 1)[0];
  const prefix = `nature_${kind}_`;
  if (!textureKey.startsWith(prefix)) return '';
  const rest = textureKey.slice(prefix.length);
  const cut = rest.lastIndexOf('_');
  return cut > 0 ? rest.slice(0, cut) : rest;
}

/** Convert a 0xRRGGBB number to an SVG `#rrggbb` hex string. */
function hex(color: number): string {
  return '#' + color.toString(16).padStart(6, '0');
}

/** A neutral brown trunk color (shared across biomes — trunks don't re-skin). */
const TRUNK_COLOR = 0x5d4037;

/** Generate a tree SVG with a brown trunk + a tinted canopy. */
function generateTreeSVG(canopyColor: number): string {
  const c = hex(canopyColor);
  const t = hex(TRUNK_COLOR);
  const svg = `<svg width="80" height="120" viewBox="0 0 80 120" xmlns="http://www.w3.org/2000/svg">
    <path d="M36 120 Q40 80 34 50 L46 50 Q40 80 44 120 Z" fill="${t}" />
    <path d="M34 50 L46 50 L40 120 Z" fill="none" stroke="${hex(0x3e2723)}" stroke-width="1.5" opacity="0.4" />
    <g transform="translate(40, 45)">
      <circle cx="0" cy="0" r="22" fill="${c}" />
      <circle cx="-16" cy="6" r="16" fill="${c}" />
      <circle cx="16" cy="6" r="16" fill="${c}" />
      <circle cx="0" cy="-14" r="16" fill="${c}" />
      <circle cx="-10" cy="-8" r="12" fill="${c}" opacity="0.85" />
      <circle cx="10" cy="-8" r="12" fill="${c}" opacity="0.85" />
    </g>
  </svg>`;
  return 'data:image/svg+xml;base64,' + (typeof btoa !== 'undefined'
    ? btoa(unescape(encodeURIComponent(svg)))
    : Buffer.from(svg).toString('base64'));
}

/** Generate a bush SVG — a low round tinted cluster. */
function generateBushSVG(foliageColor: number): string {
  const c = hex(foliageColor);
  const darker = hex(Math.round(foliageColor * 0.75));
  const svg = `<svg width="40" height="30" viewBox="0 0 40 30" xmlns="http://www.w3.org/2000/svg">
    <g transform="translate(20, 22)">
      <circle cx="0" cy="0" r="10" fill="${c}" />
      <circle cx="-9" cy="2" r="8" fill="${c}" />
      <circle cx="9" cy="2" r="8" fill="${c}" />
      <circle cx="0" cy="-6" r="8" fill="${c}" />
      <circle cx="-5" cy="-3" r="6" fill="${darker}" opacity="0.5" />
      <circle cx="5" cy="-3" r="6" fill="${darker}" opacity="0.5" />
    </g>
  </svg>`;
  return 'data:image/svg+xml;base64,' + (typeof btoa !== 'undefined'
    ? btoa(unescape(encodeURIComponent(svg)))
    : Buffer.from(svg).toString('base64'));
}

/** Generate a grass tuft SVG — a few tinted blades. */
function generateGrassSVG(foliageColor: number): string {
  const c = hex(foliageColor);
  const darker = hex(Math.round(foliageColor * 0.7));
  const svg = `<svg width="24" height="12" viewBox="0 0 24 12" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 12 Q5 4 8 1" stroke="${c}" stroke-width="2" fill="none" stroke-linecap="round" />
    <path d="M9 12 Q10 3 12 0" stroke="${darker}" stroke-width="2" fill="none" stroke-linecap="round" />
    <path d="M14 12 Q15 4 17 1" stroke="${c}" stroke-width="2" fill="none" stroke-linecap="round" />
    <path d="M19 12 Q20 5 22 2" stroke="${darker}" stroke-width="2" fill="none" stroke-linecap="round" />
    <path d="M6 12 Q7 6 9 3" stroke="${c}" stroke-width="2" fill="none" stroke-linecap="round" />
  </svg>`;
  return 'data:image/svg+xml;base64,' + (typeof btoa !== 'undefined'
    ? btoa(unescape(encodeURIComponent(svg)))
    : Buffer.from(svg).toString('base64'));
}

/** Recipe-backed templates replace the caller's `__tpl_` suffix with their
 *  content-digest suffix, so a recipe or palette edit never reuses a stale texture. */
export function vegetationTextureCacheKey(
  textureKey: string,
  kind: VegetationKind,
  canopyColor: number,
  template?: string,
  palette?: TownPropPaletteOverrides,
): string {
  const family = recipeFamilyFor(template);
  if (!family || (family.vegetationKind && family.vegetationKind !== kind)) return textureKey;
  return textureKey.split('__tpl_', 1)[0] + family.textureSuffix(template as never, townPropPalette(canopyColor, palette));
}

/** Generate (or return cached) dataUrl for a vegetation texture key.
 *  The key encodes the kind + biome + the canopy tint the SVG bakes in:
 *  `nature_<kind>_<biome>_<tintHex>`. */
export function generateVegetationTexture(
  textureKey: string,
  kind: VegetationKind,
  canopyColor: number,
  template?: string,
  palette?: TownPropPaletteOverrides,
): string {
  const effectiveKey = vegetationTextureCacheKey(textureKey, kind, canopyColor, template, palette);
  const cached = dataUrlCache.get(effectiveKey);
  if (cached) return cached;
  const dataUrl = isPropKind(kind)
    ? toDataUrl(generatePropSVG(kind, biomeFromKey(textureKey, kind), undefined, canopyColor, template, palette))
    : kind === 'tree'
      ? generateTreeSVG(canopyColor)
      : kind === 'bush'
        ? generateBushSVG(canopyColor)
        : generateGrassSVG(canopyColor);
  dataUrlCache.set(effectiveKey, dataUrl);
  return dataUrl;
}

/** Clear the texture cache (e.g. on a substrate reset). */
export function clearNatureTextureCache(): void {
  dataUrlCache.clear();
}
